import { getField, updateField } from 'vuex-map-fields'
import MultipleLine from './multipleLine'
import generateBaseImportFromStage from '@/store/baseImportFromStage'
import { lineString, lineIntersect } from '@turf/turf'
import api from '@/api/api'

export const namespaced = true

let baseUrl = '/multiple_head_lines/'

const multipleLine = new MultipleLine(baseUrl)
const baseImportFromStage = generateBaseImportFromStage({})

const getDefaultState = () => {
  return {
    ...structuredClone(multipleLine.state),
    ...structuredClone(baseImportFromStage.state)
  }
}

export const state = getDefaultState()

export const mutations = {
  ...multipleLine.mutations,
  ...baseImportFromStage.mutations,
  updateField,
  UPDATE_X_LOCATION(state, { downward_pressure, upward_pressure, ratio }) {
    state.currentItem.downward_pressure = downward_pressure
    state.currentItem.upward_pressure = upward_pressure
    state.currentItem.ratio = ratio
  }
}

export const actions = {
  ...multipleLine.actions,
  ...baseImportFromStage.actions,
  async updateXLocation({ commit, state }) {
    let updatedObj = state.dirtyObject ? state.dirtyObject : {}
    let currentObj = state.items.find((item) => item.id === updatedObj.id)
    if (updatedObj?.id && currentObj) {
      let url = baseUrl + `${updatedObj.id}/`
      let response = await api.patch(url, updatedObj)
      const { downward_pressure, upward_pressure, ratio } = response.data
      commit('UPDATE_X_LOCATION', { downward_pressure, upward_pressure, ratio })
      commit('EMPTY_DIRTY_OBJECT')
    }
  },
  async updateHeadLine2({ commit, dispatch, state }, enabled) {
    const headLine2 = state.items.find((item) => item.number === 2)
    if (headLine2) {
      let url = baseUrl + `${headLine2.id}/`
      await api.patch(url, { enabled })
      commit('SET_NESTED_PARAMETER', { item: headLine2, parameter: 'enabled', value: enabled })
      dispatch('headLine/updateOverviewHeadline2', { id: headLine2.id, enabled }, { root: true })
      dispatch(
        'headReferenceLine/updateOverviewHeadline2',
        { id: headLine2.id, enabled },
        { root: true }
      )
    }
  },

  async importHeadLineFromStage(
    { commit, dispatch, state },
    { sourceSchematisationStageId, targetSchematisationStageId, headLineNumber = 1 }
  ) {
    try {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
      const { imported_multiple_head_line, imported_head_line, imported_head_reference_line } =
        await dispatch('importFromStage', {
          import_option: {
            source_stab_schematisation_stage_id: sourceSchematisationStageId,
            target_stab_schematisation_stage_id: targetSchematisationStageId,
            head_line_number: headLineNumber
          },
          stab_schematisation_stage: targetSchematisationStageId
        })
      commit(
        'SET_ITEMS',
        state.items.map((item) =>
          item.id === imported_multiple_head_line.id ? imported_multiple_head_line : item
        )
      )
      commit('SET_CURRENT_ITEM', imported_multiple_head_line)
      const headLineStoreMapping = {
        headLine: imported_head_line,
        headReferenceLine: imported_head_reference_line
      }
      for (const [store, headLine] of Object.entries(headLineStoreMapping)) {
        commit(`${store}/REPLACE_LINE`, headLine, { root: true })
        commit(`${store}/SET_COORDINATE`, null, { root: true })
      }
    } catch (e) {
      console.log(e)
    } finally {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
    }
  }
}

export const getters = {
  ...multipleLine.getters,
  getField,
  getCurrentMultipleHeadLineNumber: (state) => {
    return state.currentItem?.number
  },
  getCurrentMultipleHeadLineIntrusionLength: (state) => {
    return state.currentItem?.intrusion_length
  },
  getXBurstCalculation: (state) => {
    return state.currentItem ? state.currentItem.x_location : 0
  },
  getHeadLine2State: (state) => {
    const headline2 = state.items.find((item) => item.number === 2)
    return headline2?.enabled ?? false
  },
  getYBurstCalculation: (state, getters, rootState, rootGetters) => {
    let x = getters.getXBurstCalculation
    let topoProfileGeometry = rootGetters['topoProfilePoint/getTopoProfileGeometry']()
    let plotDataMaxY = rootGetters['topoProfilePoint/getPlotDataMaxY']()
    if (!isFinite(plotDataMaxY)) {
      return null
    }
    let intersectLineCenter = [
      [x, -50],
      [x, plotDataMaxY]
    ]
    const line1 = lineString(topoProfileGeometry)
    const intersects = lineIntersect(line1, lineString(intersectLineCenter))
    return intersects.features.length
      ? Math.round(intersects.features[0].geometry.coordinates[1] * 100) / 100
      : null
  },
  getColorBurstCalculation: (state) => {
    let ratio = state.currentItem ? state.currentItem.ratio : 0
    return ratio > 1.0 ? 'green' : 'red'
  },
  getBurstCalculationPlotData: (state, getters) => {
    const x = getters.getXBurstCalculation
    const y = getters.getYBurstCalculation

    let style = {
      color: getters.getColorBurstCalculation,
      size: 15,
      line: {
        color: 'black',
        width: 1
      },
      symbol: 'arrow-down'
    }
    let point = {
      x: [],
      y: [],
      text: [],
      marker: {},
      mode: 'markers',
      type: 'scatter',
      name: 'Burst Calculation',
      legendgroup: 'group'
    }
    point.x = [x]
    point.y = [y]
    point.text = ['Burst calculation']
    point.marker = style

    let line = {
      x: [x, x],
      y: [y + 0.5, y + 1],
      line: { simplify: false },
      mode: 'lines+text+markers',
      hoverinfo: 'none',
      showlegend: false,
      type: 'scatter',
      name: null,
      textangle: null,
      textfont: {
        family: 'Roboto'
      },
      legendgroup: 'group'
    }
    line.line = {
      color: 'black',
      dash: 'dot',
      width: 2
    }
    line.marker = {
      color: 'black',
      size: 1,
      line: {
        color: 'black',
        width: 1
      }
    }

    return [line, point]
  }
}
